import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    buildPuzzleViewSearchParams,
    isLegacyPuzzleUiHash,
    parsePuzzleViewFromLocation,
    type PuzzleListingView,
} from "@/helpers/puzzle-view-url";

type UsePuzzleViewUrlResult = {
    puzzleView: PuzzleListingView;
    setPuzzleView: (view: PuzzleListingView, options?: { replace?: boolean; contentHash?: string }) => void;
};

/**
 * Syncs puzzle listing UI mode with `?view=` via React Router (Raptive-friendly `location.search` updates).
 */
export function usePuzzleViewUrl(): UsePuzzleViewUrlResult {
    const location = useLocation();
    const navigate = useNavigate();
    const puzzleView = parsePuzzleViewFromLocation(location);
    const setPuzzleView = useCallback(
        (view: PuzzleListingView, options?: { replace?: boolean; contentHash?: string }) => {
            const search = buildPuzzleViewSearchParams(location.search, view);
            let hash = "";
            if (options?.contentHash) {
                hash = options.contentHash.startsWith("#") ? options.contentHash : `#${options.contentHash}`;
            } else if (view === "listing" || view === "scoreboard" || view === "vertical") {
                hash = "";
            } else {
                const hashRaw = location.hash.replace(/^#/, "");
                hash = hashRaw && !isLegacyPuzzleUiHash(hashRaw) ? location.hash : "";
            }
            navigate({ pathname: location.pathname, search, hash }, { replace: options?.replace !== false });
        },
        [location.hash, location.pathname, location.search, navigate],
    );
    return { puzzleView, setPuzzleView };
}
